import { useState } from 'react'

import { astraTwMerge, Cell, day, getOnlyDate } from '~/astra'
import { useUnscheduledSurgeriesAndMetadataQueries } from '~/hooks/tanstack-query'
import { selectPractitionerValues } from '~/store/slices/surgeonSchedulerFilterSlice'
import { useStore } from '~/store/store'
import { useActivePractitioners } from '~/storm'
import { getHolidayStyles } from '~/utils/utils'

import { PoliclinicStatuses } from '../../shared'
import { DatePractitionerCell, DatesGrid, getFridayStyles, useDateStatusesCountsRow, usePractitionersSelectRow } from '../shared'
import { KeyboardInteraction } from './KeyboardInteraction/KeyboardInteraction'
import { PractitionerScheduleModal } from './Modal/PractitionerScheduleModal'
import { PractitionerCell } from './Table/PractitionerCell'
import { PractitionerRow } from './Table/PractitionerRow'

type Props = {
    isLoading: boolean
    isPreview?: boolean
}

const toDatePractitionerCell = (cell: { rowId: string; colId: string }): DatePractitionerCell => ({
    date: day(cell.colId),
    practitionerId: Number(cell.rowId),
})

export const Practitioners = ({ isLoading, isPreview }: Props) => {
    const [selectedCells, setSelectedCells] = useState<Cell[]>([])

    const vaktHeader = usePractitionersSelectRow({ label: 'Vakt', status: 'ON_DUTY' })
    const oxHeader = usePractitionersSelectRow({ label: 'Ox', status: 'ON-CALL' })

    const operasjonFooter = useDateStatusesCountsRow({ name: 'Operasjon', shortName: 'O', statuses: ['SURGERY'] })
    const dagkirurgiFooter = useDateStatusesCountsRow({ name: 'Dagkirurgi', shortName: 'D', statuses: ['DAY_SURGERY'] })
    const poliklinikkFooter = useDateStatusesCountsRow({ name: 'Poliklinikk', shortName: 'P', statuses: PoliclinicStatuses })
    const kveldspoliklinikkFooter = useDateStatusesCountsRow({
        name: 'Kveldspoliklinikk',
        shortName: 'KvP',
        statuses: ['EVENING_POLICLINIC'],
    })

    const filteredPractitionerValues = useStore(selectPractitionerValues)
    const activePractitioners = useActivePractitioners(
        undefined,
        filteredPractitionerValues.length > 0 ? { short_name: { in: filteredPractitionerValues } } : undefined
    )
    const showLocations = useStore(state => state.surgeonScheduler.showLocations)
    const showSurgeonCounts = useStore(state => state.surgeonScheduler.showSurgeonCounts)

    useUnscheduledSurgeriesAndMetadataQueries()
    const shouldHideScrollbar = showSurgeonCounts ? 'hide-scrollbars' : ''

    return (
        <DatesGrid
            name="practitioner-table"
            isLoading={isLoading}
            disableInteractivity={isPreview}
            selectedCells={selectedCells}
            onSelectionChange={setSelectedCells}
            rows={activePractitioners}
            className={shouldHideScrollbar}
            headers={[vaktHeader, oxHeader]}
            optimizeRendering={{
                cellHeight: showLocations ? 'medium' : 'short',
            }}
            body={{
                rowHeader: practitioner => <PractitionerRow practitionerUuid={practitioner.uuid} />,
                cellRender: (practitioner, { date }) => <PractitionerCell practitionerId={practitioner.id} date={getOnlyDate(date)} />,
                cellClassName: (_practitioner, { date }) => astraTwMerge('p-0', getFridayStyles(date), getHolidayStyles(date)),
            }}
            footers={isPreview ? [] : [operasjonFooter, dagkirurgiFooter, poliklinikkFooter, kveldspoliklinikkFooter]}
            modalContent={
                isPreview
                    ? undefined
                    : cells => (
                          <div className="relative h-fit min-w-[280px] overflow-auto">
                              <PractitionerScheduleModal selectedCells={cells.map(toDatePractitionerCell)} />
                          </div>
                      )
            }
            keyboardInteractionComponent={
                isPreview
                    ? undefined
                    : (cells, blockKeyInput) => <KeyboardInteraction selectedCells={cells.map(toDatePractitionerCell)} blockKeyInput={blockKeyInput} />
            }
        />
    )
}
